import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listGmailQueued,
  listGmailIgnoredRecent,
  dismissGmailMessage,
  assignGmailMessageToDossier,
  countGmailQueued,
  listDossierEmails,
} from '../../services/gmail/gmailInboxService';
import { LIVE, STATIC } from '../../lib/queryPresets';

const gmailKeys = {
  all: ['gmail-inbox'] as const,
  queued: () => [...gmailKeys.all, 'queued'] as const,
  ignored: () => [...gmailKeys.all, 'ignored'] as const,
  queuedCount: () => [...gmailKeys.all, 'queued-count'] as const,
  dossierEmails: (dossierId: string | undefined) => ['dossier-emails', dossierId] as const,
};

export function useGmailQueued() {
  return useQuery({
    ...LIVE, // file partagée entre avocats, pas de Realtime dessus
    queryKey: gmailKeys.queued(),
    queryFn: listGmailQueued,
  });
}

export function useGmailIgnoredRecent() {
  return useQuery({
    ...LIVE, // file partagée entre avocats, pas de Realtime dessus
    queryKey: gmailKeys.ignored(),
    queryFn: listGmailIgnoredRecent,
  });
}

export function useGmailQueuedCount() {
  return useQuery({
    ...LIVE, // compteur de la file partagée, pas de Realtime dessus
    queryKey: gmailKeys.queuedCount(),
    queryFn: countGmailQueued,
  });
}

export function useDismissGmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      dismissGmailMessage(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmailKeys.all });
    },
  });
}

export function useAssignGmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dossierId }: { id: string; dossierId: string }) =>
      assignGmailMessageToDossier(id, dossierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmailKeys.all });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
    },
  });
}

export function useDossierEmails(dossierId: string | undefined) {
  return useQuery({
    ...STATIC, // liste des emails d'un dossier : pas de polling justifié
    queryKey: gmailKeys.dossierEmails(dossierId),
    queryFn: () => listDossierEmails(dossierId!),
    enabled: !!dossierId,
  });
}
